import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Calendar, MessageSquare } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SecureApiService } from '../services/SecureApiService';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const ContactPage: React.FC = () => {
  useSEO('contact');
  const { isBannerVisible, bannerHeight } = useBanner();
  
  const apiService = new SecureApiService();

  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
            Let's Connect
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-7xl mx-auto leading-relaxed">
            Ready to transform your business? Whether you're interested in our AI-powered services,
            custom software development, cloud infrastructure, or enterprise blockchain solutions like Cognitive Ledger,
            we're here to help you navigate the future of digital solutions.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <article className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                <Phone className="w-6 h-6 text-white" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Quick Call
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                Speak with our team immediately
              </p>
              <a
                href="tel:+15172025360"
                className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-600/50 transition-all duration-300 text-sm"
              >
                Call Now
              </a>
            </article>

            <article className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                <MessageSquare
                  className="w-6 h-6 text-white"
                  aria-hidden="true"
                />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Email Us
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                Send us your questions anytime
              </p>
              <a
                href="mailto:team@cognitivecrafthub.com"
                className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-600/50 transition-all duration-300 text-sm"
              >
                Email Us
              </a>
            </article>

            <article className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Book Meeting
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                Schedule a free consultation
              </p>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-600/50 transition-all duration-300 text-sm"
              >
                Schedule Now
              </button>
            </article>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <ContactForm apiService={apiService} />

            <aside className="space-y-8">
              <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                  Contact Information
                </h3>
                <address className="space-y-6 not-italic">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center mr-4 mt-1">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-gray-900 dark:text-white font-medium mb-3">Our Locations</div>
                      <div className="space-y-3">
                        <div>
                          <div className="text-gray-900 dark:text-white font-medium text-sm">Headquarters</div>
                          <div className="text-gray-600 dark:text-gray-300 text-sm">
                            East Lansing, Michigan
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-900 dark:text-white font-medium text-sm">Houston Office</div>
                          <div className="text-gray-600 dark:text-gray-300 text-sm">
                            Houston, Texas
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center mr-4">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-gray-900 dark:text-white font-medium">Email</div>
                      <a
                        href="mailto:team@cognitivecrafthub.com"
                        className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        team@cognitivecrafthub.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center mr-4">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-gray-900 dark:text-white font-medium">Phone</div>
                      <a
                        href="tel:+15172025360"
                        className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        +1 (517) 202-5360
                      </a>
                    </div>
                  </div>
                </address>
              </section>

              <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                  Business Hours
                </h3>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Monday - Sunday</span>
                    <span className="text-blue-600 dark:text-blue-400">
                      5:00 AM - 10:00 PM EST
                    </span>
                  </div>
                </div>
              </section>

              <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                  Why Choose Us?
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                      24/7
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Support</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                      High
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Reliability</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                      Expert
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Support</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                      24h
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Response</div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <Link
                    to="/about"
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium"
                  >
                    Learn about our Cognitive Blueprint™ approach →
                  </Link>
                </div>
              </section>
            </aside>
          </div>
          
          {/* reCAPTCHA Legal Notice - Required when hiding badge */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This site is protected by reCAPTCHA and the Google{' '}
              <a 
                href="https://policies.google.com/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a 
                href="https://policies.google.com/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};